import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Section, StatusBadge, EmptyState } from "../components/fields";
import GoalCheckSheet from "../components/GoalCheckSheet";
import TeacherReportPanel from "../components/TeacherReportPanel";
import { GOAL_TYPES, GOAL_SCHEDULES, GOAL_RESULTS, GOAL_PROMPTS, NO_PROMPT_RESULTS } from "../lib/goalMeta";
import { Target, Plus, Download, Pencil, X, FileText, FolderDown } from "lucide-react";
import { toast } from "sonner";

const MANAGER_ROLES = ["supervisor", "sped_teacher", "tag_admin"];
const RECORDER_ROLES = ["aide", "sped_teacher", "supervisor", "tag_admin"];
const CORRECTOR_ROLES = ["supervisor", "tag_admin"];
const TYPE_TONE = { "Classroom/Academic": "info", Behavior: "warning", Adaptive: "success", Clinician: "neutral" };
const EMPTY_GOAL = { goal_type: "Classroom/Academic", area: "", text: "", target: "", iep_date: "", schedule: "biweekly", status: "Active" };

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-bold tracking-[0.06em] uppercase text-slate-500">{label}</label>
    {children}
  </div>
);
const inputCls = "mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600";

export default function GoalsPage() {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const canRecord = RECORDER_ROLES.includes(user?.role);
  const canCorrect = CORRECTOR_ROLES.includes(user?.role);
  const canExport = isManager;
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [schools, setSchools] = useState(null);
  const [studentId, setStudentId] = useState(params.get("student") || "");
  const [goals, setGoals] = useState([]);
  const [checks, setChecks] = useState([]);
  const [filters, setFilters] = useState({ aide_id: "", goal_id: "", start: "", end: "" });
  const [goalForm, setGoalForm] = useState(null);
  const [recordFor, setRecordFor] = useState(null);
  const [correcting, setCorrecting] = useState(null);
  const [reportFor, setReportFor] = useState(null);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data));
    if (user?.role === "tag_admin" || user?.role === "supervisor") api.get("/schools").then((r) => setSchools(r.data)).catch(() => setSchools(null));
  }, [user?.role]);

  const enabledSchoolIds = useMemo(() => {
    if (!schools) return null;
    return new Set(schools.filter((s) => s.settings?.goal_check_enabled).map((s) => s.id));
  }, [schools]);
  const visibleStudents = useMemo(
    () => (enabledSchoolIds ? students.filter((s) => enabledSchoolIds.has(s.school_id)) : students),
    [students, enabledSchoolIds]
  );

  const loadGoals = () => { if (studentId) api.get(`/goals?student_id=${studentId}`).then((r) => setGoals(r.data)); else setGoals([]); };
  useEffect(loadGoals, [studentId]);

  const loadChecks = () => {
    const p = { ...(studentId && { student_id: studentId }), ...(filters.aide_id && { aide_id: filters.aide_id }),
      ...(filters.goal_id && { goal_id: filters.goal_id }), ...(filters.start && { start: filters.start }), ...(filters.end && { end: filters.end }) };
    api.get("/goal-checks", { params: p }).then((r) => setChecks(r.data)).catch(() => setChecks([]));
  };
  useEffect(loadChecks, [studentId, filters]);

  const saveGoal = async () => {
    try {
      if (goalForm.id) await api.put(`/goals/${goalForm.id}`, goalForm);
      else await api.post("/goals", { ...goalForm, student_id: studentId });
      toast.success(goalForm.id ? "Goal updated" : "Goal added");
      setGoalForm(null);
      loadGoals();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportCsv = () => {
    const q = new URLSearchParams();
    if (studentId) q.set("student_id", studentId);
    if (filters.aide_id) q.set("aide_id", filters.aide_id);
    if (filters.goal_id) q.set("goal_id", filters.goal_id);
    if (filters.start) q.set("start", filters.start);
    if (filters.end) q.set("end", filters.end);
    downloadFile(`/goal-checks/export?${q.toString()}`, "goal_check_history.csv").catch((e) => toast.error(apiError(e)));
  };

  const aideOptions = useMemo(() => {
    const m = new Map();
    checks.forEach((c) => m.set(c.aide_id, c.aide_name));
    return [...m.entries()];
  }, [checks]);
  const student = students.find((s) => s.id === studentId);

  const mdY = (d) => `${d.slice(5, 7)}-${d.slice(8, 10)}-${d.slice(0, 4)}`;
  const etToday = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const rangeQuery = () => {
    const q = new URLSearchParams();
    if (filters.start) q.set("start", filters.start);
    if (filters.end) q.set("end", filters.end);
    return q.toString();
  };
  const endDate = filters.end || etToday();
  const downloadSheet = () => {
    downloadFile(`/students/${studentId}/tracking-sheet?${rangeQuery()}`,
      `${student.last_name}, ${student.first_name} – Tracking Sheet – ${mdY(endDate)}.pdf`).catch((e) => toast.error(apiError(e)));
  };
  const downloadAllSchoolId = user?.school_id || student?.school_id || (schools || []).find((s) => s.settings?.goal_check_enabled)?.id;
  const downloadAll = () => {
    downloadFile(`/schools/${downloadAllSchoolId}/tracking-sheets?${rangeQuery()}`,
      `Tracking Sheets – ${mdY(endDate)}.zip`).catch((e) => toast.error(apiError(e)));
  };

  return (
    <div className="space-y-5 stagger">
      <PageHeader title="Goal Check" subtitle="IEP goal data collection — entered by aides, managed by TAG" />

      <div className="flex items-end gap-3 flex-wrap">
        <div className="w-64">
          <Field label="Student">
            <select data-testid="goals-student-select" value={studentId} onChange={(e) => setStudentId(e.target.value)} className={inputCls + " bg-white"}>
              <option value="">Select a student…</option>
              {visibleStudents.map((s) => <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>)}
            </select>
          </Field>
        </div>
        {isManager && studentId && (
          <button data-testid="add-goal-btn" onClick={() => setGoalForm({ ...EMPTY_GOAL })}
            className="flex items-center gap-1.5 text-sm font-semibold bg-[#087F5B] hover:bg-[#066548] text-white px-4 py-2.5 rounded-[10px] transition-colors duration-150">
            <Plus size={15} /> Add goal
          </button>
        )}
        {studentId && student && (
          <>
            <button data-testid="download-tracking-sheet-btn" onClick={downloadSheet}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-4 py-2.5 rounded-[10px] transition-colors duration-150">
              <Download size={14} /> Download tracking sheet
            </button>
            <button data-testid="teacher-report-btn" onClick={() => setReportFor(student)}
              className="flex items-center gap-1.5 text-sm font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-4 py-2.5 rounded-[10px] transition-colors duration-150">
              <FileText size={14} /> Teacher report
            </button>
          </>
        )}
        {user?.role !== "aide" && downloadAllSchoolId && (
          <button data-testid="download-all-sheets-btn" onClick={downloadAll}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#667085] border border-slate-300 hover:bg-slate-50 px-4 py-2.5 rounded-[10px] transition-colors duration-150">
            <FolderDown size={14} /> Download all
          </button>
        )}
      </div>
      <p className="text-xs text-[#98A2B3] -mt-2">Downloads use the From / To dates in Progress history below (leave blank for the last two weeks).</p>

      {studentId && (
        <Section title={`Goals — ${student ? `${student.first_name} ${student.last_name}` : ""}`}>
          {goals.length === 0 ? (
            <EmptyState icon={Target} title="No goals yet" hint={isManager ? "Add this student's IEP goals to start collecting data." : "Goals will appear here once TAG adds them."} />
          ) : (
            <div className="space-y-2.5">
              {goals.map((g) => (
                <div key={g.id} data-testid={`goal-row-${g.id}`} className="border border-[#E5E9EE] rounded-xl p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#132238]">{g.area}</p>
                    <StatusBadge tone={TYPE_TONE[g.goal_type]}>{g.goal_type}</StatusBadge>
                    <StatusBadge tone={g.status === "Active" ? "success" : "neutral"}>{g.status}</StatusBadge>
                    {g.due && <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">DATA DUE</span>}
                    <span className="text-[11px] text-[#98A2B3] ml-auto">{GOAL_SCHEDULES.find(([k]) => k === g.schedule)?.[1] || g.schedule}{g.iep_date ? ` · IEP ${g.iep_date}` : ""}</span>
                  </div>
                  <p className="text-xs text-[#667085] mt-1.5 whitespace-pre-wrap">{g.text}</p>
                  {g.target && <p className="text-[11px] text-[#98A2B3] mt-1">Target: {g.target}</p>}
                  <div className="mt-2.5 flex gap-2">
                    {g.status === "Active" && canRecord && (
                      <button data-testid={`record-check-btn-${g.id}`} onClick={() => setRecordFor(student)}
                        className="text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3 py-1.5 rounded-lg transition-colors duration-150">Record check</button>
                    )}
                    {isManager && (
                      <button data-testid={`edit-goal-btn-${g.id}`} onClick={() => setGoalForm({ ...g })}
                        className="flex items-center gap-1 text-xs font-semibold text-[#667085] hover:text-[#132238] px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors duration-150"><Pencil size={12} /> Edit</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <Section title="Progress history" action={canExport && (
        <button data-testid="export-goal-checks-btn" onClick={exportCsv}
          className="flex items-center gap-1.5 text-xs font-semibold text-[#087F5B] bg-[#EAF8F3] hover:bg-[#d9f2e8] px-3.5 py-2 rounded-lg transition-colors duration-150">
          <Download size={13} /> Download CSV
        </button>
      )}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <Field label="Aide">
            <select data-testid="filter-aide-select" value={filters.aide_id} onChange={(e) => setFilters({ ...filters, aide_id: e.target.value })} className={inputCls + " bg-white"}>
              <option value="">All</option>
              {aideOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </Field>
          <Field label="Goal">
            <select data-testid="filter-goal-select" value={filters.goal_id} onChange={(e) => setFilters({ ...filters, goal_id: e.target.value })} className={inputCls + " bg-white"}>
              <option value="">All</option>
              {goals.map((g) => <option key={g.id} value={g.id}>{g.area}</option>)}
            </select>
          </Field>
          <Field label="From"><input data-testid="filter-start-input" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className={inputCls} /></Field>
          <Field label="To"><input data-testid="filter-end-input" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className={inputCls} /></Field>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-400 border-b border-slate-100">
              <th className="py-2 pr-4">Date</th><th className="pr-4">Student</th><th className="pr-4">Goal</th><th className="pr-4">Result</th><th className="pr-4">Prompt</th><th className="pr-4">Trials</th><th className="pr-4">Note</th><th className="pr-4">By</th>{canCorrect && <th></th>}</tr></thead>
            <tbody>
              {checks.map((c) => (
                <tr key={c.id} data-testid={`check-row-${c.id}`} className="border-b border-slate-50">
                  <td className="py-2 pr-4 font-mono text-xs text-slate-500">{c.date}</td>
                  <td className="pr-4 text-slate-700">{c.student_name}</td>
                  <td className="pr-4 text-slate-500">{c.goal_area}</td>
                  <td className="pr-4 font-medium text-slate-800">{c.result}</td>
                  <td className="pr-4 text-slate-500">{c.prompt_level || "—"}</td>
                  <td className="pr-4 text-slate-500">{c.trials_total ? `${c.trials_correct}/${c.trials_total} (${c.pct}%)` : "—"}</td>
                  <td className="pr-4 text-slate-500 text-xs max-w-[220px] truncate">{c.note || "—"}</td>
                  <td className="pr-4 text-slate-500 text-xs">{c.aide_name}</td>
                  {canCorrect && (
                    <td>
                      <button data-testid={`correct-check-btn-${c.id}`} onClick={() => setCorrecting({ ...c })}
                        className="text-xs font-semibold text-[#087F5B] hover:underline">Correct</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {checks.length === 0 && <p className="text-sm text-[#667085] py-4">No goal check entries match these filters.</p>}
        </div>
      </Section>

      {goalForm && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={() => setGoalForm(null)}>
          <div className="sf-sheet bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl p-5 space-y-3.5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{goalForm.id ? "Edit goal" : "Add goal"}</h3>
              <button data-testid="goal-form-cancel-btn" onClick={() => setGoalForm(null)} className="p-1.5 text-[#667085] hover:text-[#132238] rounded-lg"><X size={17} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Goal type">
                <select data-testid="goal-type-select" value={goalForm.goal_type} onChange={(e) => setGoalForm({ ...goalForm, goal_type: e.target.value })} className={inputCls + " bg-white"}>
                  {GOAL_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Goal area"><input data-testid="goal-area-input" placeholder='e.g., "Reading," "Transitions"' value={goalForm.area} onChange={(e) => setGoalForm({ ...goalForm, area: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Goal text (paste exactly from the IEP)">
              <textarea data-testid="goal-text-input" rows={4} value={goalForm.text} onChange={(e) => setGoalForm({ ...goalForm, text: e.target.value })} className={inputCls} />
            </Field>
            <Field label="Target / mastery criterion"><input data-testid="goal-target-input" placeholder='e.g., "80% accuracy, 4 of 5 opportunities"' value={goalForm.target} onChange={(e) => setGoalForm({ ...goalForm, target: e.target.value })} className={inputCls} /></Field>
            <div className="grid grid-cols-3 gap-3">
              <Field label="Source IEP date"><input data-testid="goal-iep-date-input" type="date" value={goalForm.iep_date} onChange={(e) => setGoalForm({ ...goalForm, iep_date: e.target.value })} className={inputCls} /></Field>
              <Field label="Collection schedule">
                <select data-testid="goal-schedule-select" value={goalForm.schedule} onChange={(e) => setGoalForm({ ...goalForm, schedule: e.target.value })} className={inputCls + " bg-white"}>
                  {GOAL_SCHEDULES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </Field>
              <Field label="Status">
                <select data-testid="goal-status-select" value={goalForm.status} onChange={(e) => setGoalForm({ ...goalForm, status: e.target.value })} className={inputCls + " bg-white"}>
                  <option>Active</option><option>Inactive</option>
                </select>
              </Field>
            </div>
            <button data-testid="save-goal-btn" onClick={saveGoal} disabled={!goalForm.area.trim() || !goalForm.text.trim()}
              className="w-full bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 disabled:opacity-50">
              {goalForm.id ? "Save changes" : "Add goal"}
            </button>
          </div>
        </div>
      )}

      {recordFor && <GoalCheckSheet student={recordFor} onClose={() => setRecordFor(null)} onSaved={() => { loadGoals(); loadChecks(); }} />}

      {reportFor && <TeacherReportPanel student={reportFor} onClose={() => setReportFor(null)} />}

      {correcting && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 sf-overlay flex items-end sm:items-center justify-center" onClick={() => setCorrecting(null)}>
          <div className="sf-sheet bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3.5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">Correct entry</h3>
              <button data-testid="correct-cancel-btn" onClick={() => setCorrecting(null)} className="p-1.5 text-[#667085] hover:text-[#132238] rounded-lg"><X size={17} /></button>
            </div>
            <p className="text-xs text-slate-500">{correcting.date} · {correcting.student_name} · {correcting.goal_area} · recorded by {correcting.aide_name}. The original values stay in the audit log.</p>
            <Field label="Result">
              <select data-testid="correct-result-select" value={correcting.result} onChange={(e) => setCorrecting({ ...correcting, result: e.target.value })} className={inputCls + " bg-white"}>
                {GOAL_RESULTS.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
            {!NO_PROMPT_RESULTS.includes(correcting.result) && (
              <Field label="Prompt level">
                <select data-testid="correct-prompt-select" value={correcting.prompt_level || ""} onChange={(e) => setCorrecting({ ...correcting, prompt_level: e.target.value })} className={inputCls + " bg-white"}>
                  <option value="">Select…</option>
                  {GOAL_PROMPTS.map((p) => <option key={p}>{p}</option>)}
                </select>
              </Field>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Trials correct"><input data-testid="correct-trials-correct" type="number" min="0" value={correcting.trials_correct ?? ""} onChange={(e) => setCorrecting({ ...correcting, trials_correct: e.target.value })} className={inputCls} /></Field>
              <Field label="Trials total"><input data-testid="correct-trials-total" type="number" min="0" value={correcting.trials_total ?? ""} onChange={(e) => setCorrecting({ ...correcting, trials_total: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Note"><textarea data-testid="correct-note-input" rows={2} maxLength={300} value={correcting.note || ""} onChange={(e) => setCorrecting({ ...correcting, note: e.target.value })} className={inputCls} /></Field>
            <button data-testid="save-correction-btn" onClick={async () => {
              try {
                await api.put(`/goal-checks/${correcting.id}`, {
                  goal_id: correcting.goal_id, result: correcting.result,
                  prompt_level: NO_PROMPT_RESULTS.includes(correcting.result) ? "" : correcting.prompt_level,
                  trials_correct: correcting.trials_correct === "" || correcting.trials_correct == null ? null : Number(correcting.trials_correct),
                  trials_total: correcting.trials_total === "" || correcting.trials_total == null ? null : Number(correcting.trials_total),
                  note: correcting.note || "",
                });
                toast.success("Entry corrected — original kept in audit log");
                setCorrecting(null);
                loadChecks();
              } catch (e) { toast.error(apiError(e)); }
            }}
              disabled={(!NO_PROMPT_RESULTS.includes(correcting.result) && !correcting.prompt_level) ||
                !((correcting.trials_correct === "" || correcting.trials_correct == null) === (correcting.trials_total === "" || correcting.trials_total == null) &&
                  ((correcting.trials_correct === "" || correcting.trials_correct == null) ||
                    (Number(correcting.trials_total) > 0 && Number(correcting.trials_correct) >= 0 && Number(correcting.trials_correct) <= Number(correcting.trials_total))))}
              className="w-full bg-[#087F5B] hover:bg-[#066548] text-white font-semibold rounded-[10px] py-3 text-sm transition-colors duration-150 disabled:opacity-50">
              Save correction
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
